import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ALL_FUNCTIONS_GROUP,
  ColumnAdapter,
  DateTimeColOptionsOnly,
  DateTimeCustomOption,
  DT_OPTIONS,
  FunctionColumnValue,
  KEY_ARROW_LEFT,
  KEY_ARROW_RIGHT,
  KEY_BACKSPACE,
  KEY_DELETE,
  TableAdapter,
} from '@makyoapp/makyo-common'
import { Column16, MoreVert24, Textfield } from '@makyoapp/makyo-icon/icon'
import { DateTimeInput, SelectDropdown, SimpleInput } from '@makyoapp/makyo-ui'

import { getFnColumnSettings } from '@/core/db/functions/helpers'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { NUM_REGEX } from '@/shared/constants'
import { useDebounceInput } from '@/shared/makyo-hooks'

import { RenderColumn, RenderColumnCustom } from '../RenderColumn'

interface ColumnOrCustomDdProps {
  /**
   * html id
   */
  id: string
  /**
   * allowed column type
   */
  types?: ColumnTypes[]
  /**
   * excluded column type
   * applied after allowed types
   */
  excludeTypes?: ColumnTypes[]
  renderOption?: (opt: Pick<ColumnAdapter, 'id' | 'name' | 'type'>, inToggle: boolean) => React.ReactNode

  withCustomValue?: boolean

  enterCustomPlaceholder?: string

  arrow?: boolean

  toggleClassName?: string

  hrPlaceholder?: string
  options?: ColumnAdapter[]
  table: TableAdapter
  value: FunctionColumnValue
  onChange: (value: FunctionColumnValue) => void
  customValueType?: 'text' | 'number' | 'date'
  excludeColumns?: string[]
  tableBound?: TableAdapter
  max?: number
  min?: number
  maxLength?: number
  customTypeOnly?: boolean
  withExtraOptions?: 'date'[]
  inputClassName?: string
  inputFocusClassName?: string
  column?: ColumnAdapter
  includeUnknown?: boolean
}

export function useFilterColumnTypes(
  colOpts: ColumnAdapter[] | undefined,
  table?: TableAdapter,
  types?: ColumnTypes[],
  excludeTypes?: ColumnTypes[],
  excludeColumns?: string[],
  customValueType?: 'text' | 'number' | 'date',
  includeUnknown?: boolean
) {
  const { tablesMap } = useDbClientContext()
  return useMemo(() => {
    let cols = colOpts || []

    if (table) {
      if (types && types?.length > 0) {
        table.columns.forEach((tc) => {
          if (types?.includes(tc.type)) {
            cols.push(tc)
          }

          switch (tc.type) {
            case ColumnTypes.Connection: {
              if (!tc.connections) {
                break
              }

              const foreignColumn = tablesMap.get(tc.connections.fTableId)?.columnsMap?.get(tc.connections.fColId)
              if (!foreignColumn) {
                break
              }
              if (types?.includes(foreignColumn?._Type)) {
                //TODO: Handle cyclic connections in case the foreign column type is also a connection
                cols.push(tc)
              }
              break
            }
            case ColumnTypes.Lookup: {
              if (!tc.lookup) {
                break
              }

              // We find the connection column
              const connCol = tablesMap.get(table.id)?.columnsMap?.get(tc.lookup.connColId)
              if (!connCol) {
                break
              }

              // We find the foreign column
              const foreignColumn = tablesMap.get(connCol._Connection._ForeignTableID)?.columnsMap?.get(tc.lookup.colId)
              if (!foreignColumn) {
                break
              }
              if (types?.includes(foreignColumn?._Type)) {
                //TODO: Handle cyclic connections in case the foreign column type is also a connection
                cols.push(tc)
              }
              break
            }
          }
        })
      } else if (table && !cols.length) {
        cols = table.columns
      }
    }

    if (excludeTypes && excludeTypes.length > 0) {
      cols = cols.filter((tc) => !excludeTypes.includes(tc.type))
    }
    if (excludeColumns && excludeColumns.length > 0) {
      cols = cols.filter((tc) => !excludeColumns.includes(tc.id))
    }

    return cols.filter((tc) => {
      if (tc.type === ColumnTypes.Function) {
        const fv = getFnColumnSettings(tc)

        if (fv && fv.item) {
          const item = ALL_FUNCTIONS_GROUP.find((f) => f.id === fv.item.id)
          if (item) {
            if (item.returnType && types && types.includes(item.returnType)) {
              return true
            }
            if (customValueType === 'number') {
              return item.returnType === ColumnTypes.Number
            } else if (customValueType === 'date') {
              return item.returnType === ColumnTypes.DateTime
            } else {
              if (item.returnType) {
                return [ColumnTypes.Text, ColumnTypes.Boolean, ColumnTypes.Number, ColumnTypes.Array].includes(
                  item.returnType
                )
              }
            }

            if (includeUnknown && item.returnUnknown) {
              return true
            }
          }
          return false
        } else {
          return false
        }
      }
      return ![ColumnTypes.Connection].includes(tc.type)
    })
  }, [colOpts, excludeTypes, table, types, excludeColumns, tablesMap, customValueType, includeUnknown])
}

export function ColumnOrCustomDd({
  id,
  table,
  types = [],
  renderOption,
  withCustomValue = false,
  enterCustomPlaceholder,
  arrow = true,
  toggleClassName,
  hrPlaceholder,
  excludeTypes = [],
  value: rawValue,
  onChange,
  customValueType = 'text',
  options: colOpts,
  excludeColumns,
  min,
  max,
  maxLength,
  customTypeOnly,
  withExtraOptions,
  inputClassName,
  inputFocusClassName,
  column,
  includeUnknown = false,
}: ColumnOrCustomDdProps) {
  const { t } = useTranslation()

  const selectOption: Pick<ColumnAdapter, 'name' | 'id' | 'type'> = useMemo(
    () => ({
      id: '',
      name: t('Select Column'),
      type: ColumnTypes.Text,
    }),
    [t]
  )

  const enterCustomOption: Pick<ColumnAdapter, 'name' | 'id' | 'type'> = useMemo(
    () => ({
      id: '#custom#',
      name: enterCustomPlaceholder || t('Enter custom'),
      type: ColumnTypes.Text,
    }),
    [enterCustomPlaceholder, t]
  )

  const extraDateOptions: Pick<ColumnAdapter, 'name' | 'id' | 'type'>[] = useMemo(
    () => [
      {
        id: DateTimeCustomOption.Now,
        name: t('Now'),
        type: ColumnTypes.DateTime,
      },
      {
        id: DateTimeCustomOption.Today,
        name: t('Today'),
        type: ColumnTypes.DateTime,
      },
      {
        id: DateTimeCustomOption.Tomorrow,
        name: t('Tomorrow'),
        type: ColumnTypes.DateTime,
      },
      {
        id: DateTimeCustomOption.Yesterday,
        name: t('Yesterday'),
        type: ColumnTypes.DateTime,
      },
      {
        id: DateTimeCustomOption.CurrentMonth,
        name: t('Current Month'),
        type: ColumnTypes.DateTime,
      },
      {
        id: DateTimeCustomOption.LastMonth,
        name: t('Last Month'),
        type: ColumnTypes.DateTime,
      },
      {
        id: DateTimeCustomOption.NextMonth,
        name: t('Next Month'),
        type: ColumnTypes.DateTime,
      },
      {
        id: DateTimeCustomOption.CurrentYear,
        name: t('Current Year'),
        type: ColumnTypes.DateTime,
      },
      {
        id: DateTimeCustomOption.LastYear,
        name: t('Last Year'),
        type: ColumnTypes.DateTime,
      },
      {
        id: DateTimeCustomOption.NextYear,
        name: t('Next Year'),
        type: ColumnTypes.DateTime,
      },
    ],
    [t]
  )

  const columnOptions = useFilterColumnTypes(
    colOpts,
    table,
    types,
    excludeTypes,
    excludeColumns,
    customValueType,
    includeUnknown
  )

  const options = useMemo(() => {
    const opts = []
    if (withCustomValue) {
      opts.push(enterCustomOption)
      if (!customTypeOnly) {
        opts.push(`#{${hrPlaceholder || t('Columns')}}#` as never, ...(columnOptions || []))
      }
      if (withExtraOptions?.includes('date')) {
        opts.push(`#{${hrPlaceholder || t('Date Preset')}}#` as never, ...extraDateOptions)
      }
    } else {
      opts.push(selectOption)
      if (!customTypeOnly) {
        opts.push(`#{${hrPlaceholder || t('Columns')}}#` as never, ...(columnOptions || []))
      }
      if (withExtraOptions?.includes('date')) {
        opts.push(`#{${hrPlaceholder || t('Date Preset')}}#` as never, ...extraDateOptions)
      }
    }

    return opts
  }, [
    withCustomValue,
    enterCustomOption,
    withExtraOptions,
    customTypeOnly,
    hrPlaceholder,
    t,
    extraDateOptions,
    columnOptions,
    selectOption,
  ])

  const value = useMemo(() => {
    return rawValue ? options.find((o) => o.id === rawValue.value && typeof o !== 'string') || options[0] : options[0]
  }, [options, rawValue])

  const isCustom = useMemo(() => {
    return value?.id === '#custom#'
  }, [value])

  const [customVal, cancelCustomVal, setDebCustomVal, val, setCustomVal] = useDebounceInput(
    rawValue && rawValue.isCustom === true ? rawValue.value?.toString() : ''
  )

  const handleChange = useCallback(
    (id: string | DateTimeCustomOption, val?: string) => {
      let isCustom: boolean | 'opt' = false
      let v = id
      if (id === '#custom#') {
        isCustom = true
        if (val !== undefined) {
          v = val
        } else {
          v = customVal
        }
      } else if (DT_OPTIONS.includes(id as DateTimeCustomOption)) {
        isCustom = 'opt'
        v = id
      }
      onChange({
        isCustom,
        value: v,
      })
    },
    [onChange, customVal]
  )

  useEffect(() => {
    if (customValueType !== 'date' && rawValue?.value !== customVal && rawValue?.isCustom === true) {
      handleChange('#custom#', customVal)
    }
  }, [rawValue, handleChange, customValueType, customVal])

  const ddSmall = (
    <SelectDropdown
      id={id}
      menuDefaultSize={false}
      menuClassName={'max-h-[220px] overflow-y-auto'}
      menuContainerClassName='mk-ag-sdd'
      toggleClassName={clsx(toggleClassName, 'p-0! ltr:pr-[2px]! rtl:pl-[2px]! bg-transparent!')}
      menuHeadingClassName='px-4! py-2 text-light border-t bdr'
      menuZIndex='z-305'
      value={value}
      onChange={(opt) => {
        handleChange(opt.id)
      }}
      optionLabel={(opt) => opt.name}
      withSearch
      portalMenu
      customArrow={<MoreVert24 width={16} height={16} />}
      renderOption={(opt, inToggle) => {
        if (inToggle) {
          return null
        }
        if (opt.id === '') {
          return <RenderColumnCustom name={opt.name} icon={<Column16 className='fill-primary-500' />} />
        }
        if (opt.id === '#custom#') {
          return <RenderColumnCustom name={opt.name} icon={<Textfield className='fill-primary-500' />} />
        }
        return renderOption ? renderOption(opt, inToggle) : <RenderColumn col={opt} />
      }}
      options={options}
    />
  )

  return (
    <div className='w-full min-w-0'>
      {isCustom ? (
        <div>
          {customValueType === 'date' ? (
            <div>
              <div className='inline-flex items-center w-full min-w-0'>
                <DateTimeInput
                  value={rawValue.value?.toString() || null}
                  onChange={(date) => {
                    if (!date) return
                    onChange({ isCustom: true, value: date })
                  }}
                  showTime={(column?.settings as DateTimeColOptionsOnly)?.dtDisplay !== 'date'}
                  menuClassName='z-[302]'
                  clearable={false}
                />

                <SelectDropdown
                  id={id}
                  menuDefaultSize={false}
                  menuClassName={'max-h-[220px] overflow-y-auto'}
                  menuContainerClassName='mk-ag-sdd'
                  toggleClassName={clsx(toggleClassName, 'p-0! ltr:pr-[2px]! rtl:pl-[2px]! bg-transparent!')}
                  menuHeadingClassName='px-4! py-2 text-light border-t bdr'
                  menuZIndex='z-305'
                  value={value}
                  onChange={(opt) => {
                    handleChange(opt.id)
                  }}
                  optionLabel={(opt) => opt.name}
                  withSearch
                  portalMenu
                  customArrow={<MoreVert24 width={16} height={16} />}
                  renderOption={(opt, inToggle) => {
                    if (inToggle) {
                      return null
                    }
                    if (opt.id === '') {
                      return <RenderColumnCustom name={opt.name} icon={<Column16 className='fill-primary-500' />} />
                    }
                    if (opt.id === '#custom#') {
                      return <RenderColumnCustom name={opt.name} icon={<Textfield className='fill-primary-500' />} />
                    }
                    return renderOption ? renderOption(opt, inToggle) : <RenderColumn col={opt} />
                  }}
                  options={options}
                />
              </div>
            </div>
          ) : (
            <SimpleInput
              placeholder={t('Enter Custom')}
              value={val}
              onChange={(e) => {
                let val = e.currentTarget.value
                if (maxLength !== undefined && val.length > maxLength) {
                  val = val.slice(maxLength)
                }
                cancelCustomVal()
                setCustomVal(val)
                //handleChange(value, val)
              }}
              min={min}
              max={max}
              onPaste={(e) => {
                const data = e.clipboardData.getData('Text')
                //const val = e.currentTarget.value
                if (customValueType === 'number' && !NUM_REGEX.test(data)) {
                  e.preventDefault()
                }
              }}
              onBlur={(e) => {
                let val = e.currentTarget.value
                if (maxLength !== undefined && val.length > maxLength) {
                  val = val.slice(maxLength)
                }

                if (customValueType === 'number') {
                  let numVal = Number(val)
                  if (max !== undefined && numVal > max) {
                    numVal = max
                  }
                  if (min !== undefined && numVal < min) {
                    numVal = min
                  }
                  if (val) {
                    setCustomVal(numVal.toString())
                    cancelCustomVal()
                    setDebCustomVal(numVal.toString())
                    //handleChange(value, numVal.toString())
                  }
                }
              }}
              onKeyDown={(e) => {
                if (customValueType === 'number') {
                  if (
                    !NUM_REGEX.test(e.key) &&
                    !e.ctrlKey &&
                    ![KEY_BACKSPACE, KEY_DELETE, KEY_ARROW_LEFT, KEY_ARROW_RIGHT].includes(e.key)
                  ) {
                    e.preventDefault()
                  }
                }
              }}
              className={inputClassName}
              inputFocusClassName={inputFocusClassName}
              inputClassName='min-w-0'
              type={customValueType}
              after={customTypeOnly !== true || withExtraOptions ? ddSmall : undefined}
            />
          )}
        </div>
      ) : (
        <SelectDropdown
          className='w-full'
          id={id}
          menuDefaultSize={false}
          menuClassName={'max-h-[220px] overflow-y-auto'}
          menuContainerClassName='mk-ag-sdd'
          toggleClassName={clsx(toggleClassName)}
          menuHeadingClassName='px-4! py-2 text-light border-t bdr'
          menuZIndex='z-305'
          value={value}
          onChange={(opt) => {
            handleChange(opt.id)
          }}
          optionLabel={(opt) => opt.name}
          withSearch
          portalMenu
          customArrow={<MoreVert24 width={16} height={16} />}
          arrow={arrow}
          renderOption={(opt, inToggle) => {
            if (!opt) {
              return null
            }
            if (opt.id === '#select#') {
              return <RenderColumnCustom name={opt.name} icon={<Column16 className='fill-primary-500' />} />
            }
            if (opt.id === '#custom#') {
              return <RenderColumnCustom name={opt.name} icon={<Textfield className='fill-primary-500' />} />
            }
            if (opt.id.startsWith('#') && opt.id.endsWith('#')) {
              return <RenderColumn col={opt} />
            }
            return renderOption ? renderOption(opt, inToggle) : <RenderColumn col={opt} />
          }}
          options={options}
        />
      )}
    </div>
  )
}
